/**
 * Typed query key factory.
 *
 * Creates a structured, hierarchical key namespace for a given domain.
 * All keys are `as const` tuples, giving TanStack Query full type information
 * for cache operations (invalidation, prefetching, selection).
 *
 * Usage:
 *   const userKeys = createQueryKeys("users");
 *   userKeys.all              // ["users"]
 *   userKeys.lists()          // ["users", "list"]
 *   userKeys.list({ page: 1 }) // ["users", "list", { page: 1 }]
 *   userKeys.details()        // ["users", "detail"]
 *   userKeys.detail("abc")    // ["users", "detail", "abc"]
 *
 * SSE invalidation example:
 *   queryClient.invalidateQueries({ queryKey: userKeys.all });
 *   queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
 */
export function createQueryKeys<TDomain extends string>(domain: TDomain) {
  return {
    /** Matches ALL queries for this domain. Use for broad invalidation. */
    all: [domain] as const,

    /** Matches all list queries for this domain. */
    lists: () => [domain, "list"] as const,

    /** Matches a specific list query with the given params. */
    list: <TParams>(params: TParams) => [domain, "list", params] as const,

    /** Matches all detail queries for this domain. */
    details: () => [domain, "detail"] as const,

    /** Matches a specific detail query for the given id. */
    detail: <TId>(id: TId) => [domain, "detail", id] as const,
  };
}

export type QueryKeys<TDomain extends string> = ReturnType<typeof createQueryKeys<TDomain>>;
